// Server-backed store for in-progress application data.
//
// Public API (setItem/getItem/removeItem) is intentionally SYNCHRONOUS and
// unchanged, so the many components that use it need no edits. Internally the
// data lives in an in-memory cache that is the synchronous source of truth,
// and is persisted to an encrypted server-side draft (keyed by an httpOnly
// cookie) so it survives a page refresh WITHOUT keeping PII in browser storage.
//
// Lifecycle:
//   - On construction we hydrate the cache from the server draft (`ready`).
//   - setItem/removeItem update the cache immediately and schedule a debounced
//     save of the whole cache to the server.
//   - Call flush() to persist immediately (e.g. on step navigation / submit).
//   - Call clearDraft() after a successful submission to discard the draft.
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';

interface DraftMeta {
  role?: string;
  applicationGuid?: string;
  invitationToken?: string;
}

interface DraftResponseData {
  applicationGuid?: string | null;
  role?: string | null;
  invitationToken?: string | null;
  payload?: Record<string, unknown> | null;
}

/** Body shape shared by draft PUT and application submit. */
export interface DraftBody {
  invitationToken: string | null;
  applicationGuid: string | null;
  role: string | null;
  payload: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class DataStorageService {
  private readonly http = inject(HttpClient);
  private readonly draftUrl = `${environment.apiUrl}/applications/draft`;
  private readonly SAVE_DEBOUNCE_MS = 800;

  /** Synchronous source of truth. */
  private readonly cache = new Map<string, unknown>();

  /** Metadata sent alongside the payload so the draft is self-describing. */
  private meta: DraftMeta = {};

  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private hydrated = false;

  /** Resolves once the initial hydrate from the server has completed. */
  public readonly ready: Promise<void>;

  constructor() {
    this.ready = this.hydrate();
  }

  // ----- Public synchronous API (unchanged signatures) -----

  public setItem(key: string, value: unknown): void {
    this.cache.set(key, this.clone(value));
    this.scheduleSave();
  }

  public getItem<T>(key: string): T | null {
    if (!this.cache.has(key)) {
      return null;
    }
    return this.clone(this.cache.get(key)) as T;
  }

  public removeItem(key: string): void {
    this.cache.delete(key);
    this.scheduleSave();
  }

  // ----- Draft lifecycle -----

  /** Canonical application GUID from the server-backed draft (cookie-keyed). */
  public getApplicationGuid(): string | null {
    return this.meta.applicationGuid ?? null;
  }

  /** Provide draft metadata (role/guid/token) once the invitation is known. */
  public setDraftMeta(meta: DraftMeta): void {
    let changed = false;
    for (const k of ['role', 'invitationToken'] as const) {
      const v = meta[k];
      if (v && v !== this.meta[k]) {
        this.meta[k] = v;
        changed = true;
      }
    }
    // Only adopt applicationGuid when local meta doesn't already have one —
    // draft/server is the durable source; never overwrite a hydrated GUID.
    if (meta.applicationGuid && !this.meta.applicationGuid) {
      this.meta.applicationGuid = meta.applicationGuid;
      changed = true;
    }
    if (changed) {
      this.scheduleSave();
    }
  }

  /**
   * Ensure the draft is hydrated and has a server-minted applicationGuid.
   * Flushes once if needed so Plaid/submit never run against a null GUID.
   * Refuses to mint without identifying meta (role and/or invitationToken).
   */
  public async ensureApplicationGuid(): Promise<string | null> {
    await this.ready;
    if (this.meta.applicationGuid) {
      return this.meta.applicationGuid;
    }
    if (!this.hasIdentifyingMeta()) {
      return null;
    }
    await this.flush();
    return this.meta.applicationGuid ?? null;
  }

  /** Load the server-side draft into the cache. Safe to await repeatedly. */
  private async hydrate(): Promise<void> {
    if (this.hydrated) {
      return;
    }
    try {
      const res = await this.http
        .get<{ data: DraftResponseData } | null>(this.draftUrl, { withCredentials: true })
        .toPromise();

      const data = res?.data;
      if (data) {
        this.meta = {
          role: data.role ?? this.meta.role,
          applicationGuid: data.applicationGuid ?? this.meta.applicationGuid,
          invitationToken: data.invitationToken ?? this.meta.invitationToken,
        };
        if (data.payload && typeof data.payload === 'object') {
          for (const [key, value] of Object.entries(data.payload)) {
            this.cache.set(key, value);
          }
        }
      }
    } catch (err) {
      // No draft (204) or a transient error: start empty rather than blocking the app.
      console.warn('Draft hydrate failed or no draft present:', err);
    } finally {
      this.hydrated = true;
    }
  }

  private scheduleSave(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
    this.saveTimer = setTimeout(() => this.flush(), this.SAVE_DEBOUNCE_MS);
  }

  /**
   * Snapshot of the current draft in the same shape used by PUT /applications/draft
   * and POST /applications/{role}/submit.
   */
  public getDraftBody(): DraftBody {
    const payload: Record<string, unknown> = {};
    for (const [key, value] of this.cache.entries()) {
      payload[key] = this.clone(value);
    }
    return {
      invitationToken: this.meta.invitationToken ?? null,
      applicationGuid: this.meta.applicationGuid ?? null,
      role: this.meta.role ?? null,
      payload,
    };
  }

  /**
   * Persist the whole cache to the server draft immediately. Returns true on success.
   * Skips the network call when there is no identifying meta (avoids empty PUTs).
   */
  public async flush(): Promise<boolean> {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    // Never write before hydration completes, or we could clobber the stored draft.
    await this.ready;

    if (!this.hasIdentifyingMeta()) {
      return false;
    }

    try {
      const res = await this.http
        .put<{ data?: DraftResponseData }>(this.draftUrl, this.getDraftBody(), { withCredentials: true })
        .toPromise();
      // Adopt server-minted GUID from the PUT response (draft is the durable source).
      if (res?.data?.applicationGuid) {
        this.meta.applicationGuid = res.data.applicationGuid;
      }
      return true;
    } catch (err) {
      console.error('Draft save failed:', err);
      return false;
    }
  }

  /** True when meta has enough to key/mint a draft (role, invitationToken, or applicationGuid). */
  private hasIdentifyingMeta(): boolean {
    return !!(this.meta.invitationToken || this.meta.role || this.meta.applicationGuid);
  }

  /** Discard the server draft and clear the local cache (after submission). */
  public async clearDraft(): Promise<void> {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    this.cache.clear();
    this.meta = {};
    try {
      await this.http.delete(this.draftUrl, { withCredentials: true }).toPromise();
    } catch (err) {
      console.error('Draft clear failed:', err);
    }
  }

  private clone<T>(value: T): T {
    if (value === null || value === undefined || typeof value !== 'object') {
      return value;
    }
    return JSON.parse(JSON.stringify(value)) as T;
  }
}
